/**
 * useMapStyling.ts
 * يجهّز تنسيق الخريطة عند التحميل (خلفية/حدود/تضاريس/سواحل)، ويعيد تطبيقه عند تبديل
 * الثيم، ويحسب أدنى زووم يملأ العرض بلا تكرار. يُرجع معالِجَي onLoad و onMoveEnd.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type { Map as MaplibreMap } from 'maplibre-gl';
import {
    liftAdminBordersAboveBase, hideBaseMapLabels, styleAdminBorders, styleWeatherBase,
    addContinentCoastline, addTerrainHillshade, setAerialImagery,
} from '../webgl/layerOrder';
import type { GridBounds } from '../utils/gridBounds';
import { getMinZoom, getMaxZoom } from '../../../config/mapZoom';
import { imagerySources } from '../../../config/basemapImagery';

interface Params {
    mapRef: React.RefObject<MapRef | null>;
    darkMode: boolean;
    /** الطبقة الفعّالة — تحدّد لون القاعدة الفاتحة (الضغط = رمادي بدل الفستقي). */
    activeLayerKey?: string | null;
    /** طبقة القمر الصناعي فعّالة؟ تُعامَل كطبقة كاملة التغطية في تنسيق القاعدة. */
    satelliteActive?: boolean;
    /** طبقة الرادار فعّالة؟ قاعدة قائمة بذاتها ولها لونها الخاصّ في styleWeatherBase. */
    radarActive?: boolean;
    /** خيار المستخدم: خلفية صور جوّية بدل القاعدة الرمادية (على الرادار وحده). */
    aerialImagery?: boolean;
    /** قالب ألوان الأمطار الثاني نشط؟ يجعل خلفية الخريطة رمادية بدل الزيتوني/الكحلي. */
    precipGrayBase?: boolean;
    setMapBounds: (b: GridBounds) => void;
    setZoomLevel: (z: number) => void;
}

/**
 * حدّا الزووم الفعليان — المصدر الوحيد للتقييد (يدوسان ما يُمرَّر كخاصية للخريطة).
 *
 * الأدنى: الأعلى بين حدّ الجهاز وأدنى زووم يجعل العالم يملأ **عرض** الكانفاس
 * و**ارتفاعه** معاً. حساب العرض وحده لم يكن كافياً على الشاشات العريضة: كان يُسمح
 * بالإبعاد حتى يصير عرض العالم أصغر من النافذة فتظهر أفريقيا مكرّرة مرّتين أو ثلاثاً
 * (نسخ renderWorldCopies). خريطة Mercator مربّعة عند أي زووم، فأخذ الأكبر بين
 * البعدين يضمن نسخة واحدة تملأ الشاشة تماماً — وهو سلوك Zoom Earth.
 *
 * الأعلى: getMaxZoom().
 */
function applyZoomLimits(map: MaplibreMap): void {
    const canvas = map.getCanvas();
    // clientWidth/clientHeight بوحدات CSS — نفس وحدة معادلة MapLibre (512 بكسل/بلاطة).
    // قد تكون صفراً إن استُدعيت قبل تخطيط الحاوية، فنتجاهل الحساب حينها ونكتفي بحدّ
    // الجهاز؛ يُعاد الضبط عند أوّل حدث resize.
    const w = canvas.clientWidth  || 0;
    const h = canvas.clientHeight || 0;
    const fitWorldZoom = w > 0 && h > 0
        ? Math.max(Math.log2(w / 512), Math.log2(h / 512))
        : -Infinity;
    const min = Math.max(getMinZoom(), fitWorldZoom);
    map.setMinZoom(min);
    // الحدّ الأعلى يتبع حجم الشاشة أيضاً، فيتبدّل عند تدوير الجهاز أو تغيير حجم
    // النافذة (عبور عتبة 679 بكسل) بلا إعادة تحميل.
    map.setMaxZoom(getMaxZoom());
    // setMinZoom يرفع الزووم الحالي تلقائياً، لكن ذلك لا يحدث إن كان الحدّ ثابتاً
    // ثم كبرت النافذة، فنُجبره صراحةً.
    if (map.getZoom() < min) map.setZoom(min);
}

export function useMapStyling({ mapRef, darkMode, activeLayerKey, satelliteActive, radarActive, aerialImagery, precipGrayBase, setMapBounds, setZoomLevel }: Params) {
    /*
     * الصور مشروطةٌ بثلاثة معاً: طبقة الرادار، وخيار المستخدم، ووجود مصدرٍ يعمل.
     *
     * الثالث هو ما يحقّق الاتّفاق: حين تنفد حصّة Mapbox تُفرَّغ `imagerySources()`
     * فيصير `wantImagery` كاذباً، فتعود القاعدة الرمادية بنفس المسار الذي يسلكه
     * إطفاءُ المستخدم للخيار — لا مسار طوارئ منفصل قد ينسى شيئاً.
     */
    const wantImagery = !!radarActive && aerialImagery !== false && imagerySources().length > 0;
    const initedRef = useRef(false);
    // عدّاد يتقدّم عند سقوط Mapbox، لا لقيمته بل ليُعيد تشغيل مؤثّر التنسيق: عندها
    // يُعاد حساب `wantImagery` (وقد صار كاذباً) فتُستعاد القاعدة الرمادية.
    const [imageryEpoch, setImageryEpoch] = useState(0);

    /**
     * مفتاح الطبقة **لأغراض التنسيق وحده** — منفصل عن activeLayerKey عمداً.
     *
     * صور القمر طبقة كاملة التغطية ومعتمة، لكنها ليست حقلاً عددياً فلا تدخل في
     * activeHeatmapType، فيصل التنسيق `null` ⇒ يظنّ styleWeatherBase أنه «لا طبقة» ⇒
     * يُبقي مضلّعات اليابسة والبحر معتمة 10-12% بلون داكن فوق الصور = حجاب أسود يغطّي
     * الخريطة كلّها. تمرير 'satellite' هنا يضعها في مسار «كاملة التغطية» فتصير المضلّعات
     * شفّافة تماماً وتظهر الصور نقيّة.
     *
     * ولا نغيّر activeLayerKey نفسه لأنه يغذّي أيضاً قارئ التلميح (hoverType) ومفاتيح
     * الجسيمات والإيزوبار، وكلّها تتوقّع نوع حقل عددي لا اسم طبقة صور.
     */
    // الرادار كالقمر: طبقة بلاطات لا حقل عددي، فلا يظهر في activeLayerKey — ويجب مع ذلك
    // أن يصل إلى styleWeatherBase باسمه ليأخذ قاعدته. بلا هذا السطر يسقط على الداكن العامّ.
    const styleKey = activeLayerKey ?? (satelliteActive ? 'satellite' : radarActive ? 'radar' : null);

    const handleMapLoad = useCallback(() => {
        if (initedRef.current) return; // يمنع التهيئة المزدوجة (مثلاً عند سباق React.StrictMode)
        const map = mapRef.current;
        if (!map) return;
        const gl = map.getMap();
        if (!gl.isStyleLoaded()) return; // الستايل لم يجهز فعلياً بعد — يُعاد المحاولة عبر آلية المراقبة أدناه
        initedRef.current = true;

        // كل خطوة في حارس مستقل: فشل واحدة (مثلاً التضاريس) يجب ألّا يُلغي البقية —
        // وإلّا تبقى الأسماء الإنجليزية ظاهرة وتختفي السواحل/التفاصيل.
        const step = (fn: () => void) => { try { fn(); } catch { /* تجاهل */ } };
        // الصور **قبل** تنسيق القاعدة: styleWeatherBase تُشفّف المضلّعات لتظهر الصور،
        // ولا معنى لتشفيفها قبل أن توجد الطبقة تحتها.
        step(() => setAerialImagery(gl, wantImagery, () => setImageryEpoch((n) => n + 1)));
        step(() => styleWeatherBase(gl, darkMode, styleKey, precipGrayBase, wantImagery));
        step(() => liftAdminBordersAboveBase(gl));
        step(() => hideBaseMapLabels(gl));
        step(() => styleAdminBorders(gl, darkMode, styleKey));
        step(() => addContinentCoastline(gl, darkMode, styleKey));
        // التضاريس تُؤجَّل إلى ما بعد أوّل سكون: جرد الشبكة أظهر **28 بلاطة ارتفاع**
        // (~120 كيلوبايت لكل واحدة) تُطلَب أثناء الإقلاع فتزاحم نُسج الطقس على نفس
        // الوصلة — وهي زينة لا يراها المستخدم قبل أن تظهر الألوان أصلاً. بعد idle
        // تكون الطبقة الملوّنة قد رُسمت، فتُحمَّل التضاريس على مهل بلا مزاحمة.
        gl.once('idle', () => { try { addTerrainHillshade(gl, darkMode, styleKey); } catch { /* تجاهل */ } });

        applyZoomLimits(gl);
        const b = map.getBounds();
        setMapBounds({ north: b.getNorth(), south: b.getSouth(), east: b.getEast(), west: b.getWest() });
        setZoomLevel(map.getZoom());
    }, [mapRef, setMapBounds, setZoomLevel, darkMode, styleKey, precipGrayBase, wantImagery]);

    // احتياط: حدث onLoad من react-map-gl قد لا يُطلَق (سباق معروف مع React.StrictMode
    // الذي يُركّب/يُفكّك/يُعيد تركيب الخريطة مرّتين في وضع التطوير)، فتبقى mapBounds
    // فارغة للأبد ولا تُجلَب بيانات الطقس أبداً. نراقب جهوزية الستايل صراحةً كحلّ بديل.
    useEffect(() => {
        if (initedRef.current) return;
        const id = window.setInterval(() => {
            if (initedRef.current) { window.clearInterval(id); return; }
            const gl = mapRef.current?.getMap();
            if (gl?.isStyleLoaded()) {
                handleMapLoad();
                window.clearInterval(id);
            }
        }, 150);
        return () => window.clearInterval(id);
    }, [mapRef, handleMapLoad]);

    const handleMoveEnd = useCallback(() => {
        const map = mapRef.current;
        if (!map) return;
        const b = map.getBounds();
        setMapBounds({ north: b.getNorth(), south: b.getSouth(), east: b.getEast(), west: b.getWest() });
        setZoomLevel(map.getZoom());
    }, [mapRef, setMapBounds, setZoomLevel]);

    // إعادة تطبيق التنسيق عند تبديل الثيم
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        try {
            const gl = map.getMap();
            setAerialImagery(gl, wantImagery, () => setImageryEpoch((n) => n + 1));
            styleWeatherBase(gl, darkMode, styleKey, precipGrayBase, wantImagery);
            hideBaseMapLabels(gl);          // إبقاء الأسماء الإنجليزية مخفيّة بعد تبديل الثيم
            styleAdminBorders(gl, darkMode, styleKey);
            addContinentCoastline(gl, darkMode, styleKey);
            addTerrainHillshade(gl, darkMode, styleKey);
        } catch { /* تجاهل */ }
    }, [darkMode, styleKey, precipGrayBase, wantImagery, imageryEpoch, mapRef]);

    // إعادة حساب حدّي الزووم عند تغيير حجم النافذة.
    //
    // حدث window.resize وحده لا يكفي: عند الإقلاع قد يُستدعى applyZoomLimits والكانفاس
    // ما زال بعرض صفر أو بعرض الحاوية قبل تمدّدها، فيُحسب حدّ أدنى خاطئ (0) ولا يصحَّح
    // أبداً ما لم يُغيّر المستخدم حجم النافذة — وهذا هو سبب ظهور العالم مكرّراً على
    // الشاشات الكبيرة. ResizeObserver على الكانفاس نفسه يلتقط كل تغيّر فعلي في المقاس.
    useEffect(() => {
        const onResize = () => {
            const gl = mapRef.current?.getMap();
            if (gl) applyZoomLimits(gl);
        };
        window.addEventListener('resize', onResize);

        let ro: ResizeObserver | null = null;
        const canvas = mapRef.current?.getMap?.().getCanvas?.();
        if (canvas && typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(onResize);
            ro.observe(canvas);
        }
        return () => {
            window.removeEventListener('resize', onResize);
            ro?.disconnect();
        };
    }, [mapRef]);

    return { handleMapLoad, handleMoveEnd };
}
